import { useState, useEffect, useMemo } from 'react';
import { View, MapMarker, ConnectionLine, MapCoordinates } from '../types';
import { isValidCoordinate, parseCoordinates, logCoordinateStats } from '../utils/coordinates';

// Enhanced geocoding function with caching and real Nominatim API
const geocodeCache = new Map<string, MapCoordinates>();
let lastGeocodingTime = 0;

const geocodeLocation = async (location: string, state: string, country: string, existingLat?: string, existingLng?: string): Promise<MapCoordinates> => {
  // If coordinates are already provided, use them
  if (existingLat && existingLng) {
    const lat = parseFloat(existingLat);
    const lng = parseFloat(existingLng);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  const cacheKey = `${location}, ${state}, ${country}`;
  
  // Check cache first
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }

  try {
    // Use real geocoding with Nominatim (OpenStreetMap)
    const coordinates = await geocodeWithNominatim(location, state, country);
    geocodeCache.set(cacheKey, coordinates);
    return coordinates;
  } catch (error) {
    console.error(`Failed to geocode ${cacheKey}:`, error);
    // Fallback to mock coordinates if real geocoding fails
    const fallbackCoords = getMockCoordinates(location, state, country);
    geocodeCache.set(cacheKey, fallbackCoords);
    return fallbackCoords;
  }
};

// Real geocoding using Nominatim API
const geocodeWithNominatim = async (location: string, state: string, country: string): Promise<MapCoordinates> => {
  // Rate limiting: Nominatim allows max 1 request per second
  const now = Date.now();
  const timeSinceLastRequest = now - lastGeocodingTime;
  if (timeSinceLastRequest < 1000) {
    await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastRequest));
  }
  lastGeocodingTime = Date.now();

  // Construct search query - format: "location, state, country"
  const query = `${location}, ${state}, ${country}`;
  const encodedQuery = encodeURIComponent(query);
  
  const url = `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=1&addressdetails=1`;
  
      // Geocoding in progress
  
  const response = await fetch(url, {
    headers: {
      'User-Agent': 'TheViewShed/1.0 (https://tvs-phase-2.laikadynamics.com/)'
    }
  });

  if (!response.ok) {
    throw new Error(`Nominatim API error: ${response.status}`);
  }

  const data = await response.json();
  
  if (data && data.length > 0) {
    const result = data[0];
    const coordinates = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon)
    };
    
          // Geocoding successful
    return coordinates;
  } else {
    throw new Error(`No results found for ${query}`);
  }
};

// Mock geocoding with realistic US coordinates
const getMockCoordinates = (location: string, state: string, country: string): MapCoordinates => {
  const locationMap: Record<string, MapCoordinates> = {
    'Mount Washington': { lat: 44.2706, lng: -71.3033 },
    'Mount Katahdin': { lat: 45.9044, lng: -69.2797 },
    'Empire State Building': { lat: 40.7484, lng: -73.9857 },
    'One World Trade Center': { lat: 40.7127, lng: -74.0134 },
    'Golden Gate Bridge': { lat: 37.8199, lng: -122.4783 },
    'Statue of Liberty': { lat: 40.6892, lng: -74.0445 },
    'Mount Rushmore': { lat: 43.8791, lng: -103.4591 },
    'Grand Canyon': { lat: 36.1069, lng: -112.1129 },
  };

  // Check for exact match first
  if (locationMap[location]) {
    return locationMap[location];
  }

  // Generate coordinates based on state for unknown locations
  const stateCoordinates: Record<string, MapCoordinates> = {
    'New Hampshire': { lat: 43.1939, lng: -71.5724 },
    'Maine': { lat: 44.3106, lng: -69.7795 },
    'New York': { lat: 40.7128, lng: -74.0060 },
    'California': { lat: 36.7783, lng: -119.4179 },
    'South Dakota': { lat: 43.9695, lng: -99.9018 },
    'Arizona': { lat: 34.0489, lng: -111.0937 },
  };

  if (stateCoordinates[state]) {
    const base = stateCoordinates[state];
    // Add some random variation for unknown locations
    return {
      lat: base.lat + (Math.random() - 0.5) * 2,
      lng: base.lng + (Math.random() - 0.5) * 2
    };
  }

  // Fallback to US center with random variation
  return {
    lat: 39.8283 + (Math.random() - 0.5) * 10,
    lng: -98.5795 + (Math.random() - 0.5) * 20
  };
};

// Calculate distance between two coordinates (Haversine formula)
const calculateDistance = (coord1: MapCoordinates, coord2: MapCoordinates): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLng = (coord2.lng - coord1.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

interface UseMapDataReturn {
  markers: MapMarker[];
  connections: ConnectionLine[];
  isLoading: boolean;
  error: string | null;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  stats: {
    totalViews: number;
    totalDistance: number;
    averageDistance: number;
    statusCounts: Record<string, number>;
  };
}

export const useMapData = (views: View[]): UseMapDataReturn => {
  const [markers, setMarkers] = useState<MapMarker[]>([]);
  const [connections, setConnections] = useState<ConnectionLine[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Process views into map data
  useEffect(() => {
    if (views.length === 0) {
      setMarkers([]);
      setConnections([]);
      return;
    }

    const processViews = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Analyze coordinate coverage first
        logCoordinateStats(views);
        
        const newMarkers: MapMarker[] = [];
        const newConnections: ConnectionLine[] = [];

        // Count how many locations have coordinates vs need geocoding
        let locationsWithCoords = 0;
        let locationsNeedingGeocode = 0;
        
        views.forEach(view => {
          const hasSourceCoords = isValidCoordinate(view.source_lat || '', view.source_long || '');
          const hasDestCoords = isValidCoordinate(view.dest_lat || '', view.dest_long || '');
          
          if (hasSourceCoords) locationsWithCoords++;
          else locationsNeedingGeocode++;
          
          if (hasDestCoords) locationsWithCoords++;
          else locationsNeedingGeocode++;
        });

        const coordinateCoverage = locationsWithCoords / (locationsWithCoords + locationsNeedingGeocode);
        // Coordinate coverage calculated

        // If we have good coordinate coverage (>60%), skip geocoding entirely to show map immediately
        if (coordinateCoverage >= 0.6) {
                // FAST MODE: Showing map immediately with available coordinates
          
          // Process only views that have both source and destination coordinates
          const viewsWithCompleteCoords = views.filter(view => {
            const hasSourceCoords = isValidCoordinate(view.source_lat || '', view.source_long || '');
            const hasDestCoords = isValidCoordinate(view.dest_lat || '', view.dest_long || '');
            return hasSourceCoords && hasDestCoords;
          });
          
          // Processing views with complete coordinates
          
          // Process views with coordinates quickly
          for (const view of viewsWithCompleteCoords) {
            try {
              // Use backend coordinates directly (no geocoding needed)
              const sourceCoords = parseCoordinates(view.source_lat!, view.source_long!)!;
              const destCoords = parseCoordinates(view.dest_lat!, view.dest_long!)!;

              // Using backend coordinates

              // Create markers and connections
              newMarkers.push(
                {
                  id: `${view.id}-source`,
                  position: sourceCoords,
                  type: 'source',
                  viewId: view.id,
                  title: view.source,
                  location: `${view.source}, ${view.state}`,
                  status: view.status,
                  color: view.color || '#3b82f6'
                },
                {
                  id: `${view.id}-dest`,
                  position: destCoords,
                  type: 'destination',
                  viewId: view.id,
                  title: view.destination,
                  location: `${view.destination}, ${view.destinationState}`,
                  status: view.status,
                  color: view.color || '#3b82f6'
                }
              );

              newConnections.push({
                id: view.id,
                sourcePosition: sourceCoords,
                destinationPosition: destCoords,
                distance: parseFloat(view.distance) || calculateDistance(sourceCoords, destCoords),
                status: view.status,
                color: view.color || '#3b82f6',
                viewData: view
              });

            } catch (viewError) {
              console.warn(`⚠️ Failed to process view ${view.id}:`, viewError);
            }
          }
          
          // Set results immediately
          setMarkers(newMarkers);
          setConnections(newConnections);
          setIsLoading(false);
          // Map displayed in FAST MODE
          return;
        }

        // Fallback to geocoding mode for low coordinate coverage
        // GEOCODING MODE: Need to geocode missing coordinates
        
        // Process views in smaller batches for real geocoding
        const batchSize = 10; // Smaller batches for real API calls
        const totalBatches = Math.ceil(views.length / batchSize);
        
                  // Processing views with geocoding in batches

        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          const batchStart = batchIndex * batchSize;
          const batchEnd = Math.min(batchStart + batchSize, views.length);
          const batch = views.slice(batchStart, batchEnd);
          
                      // Processing batch
          
          // Process this batch
          for (const view of batch) {
            try {
              // Geocode source and destination with existing coordinates if available
              const [sourceCoords, destCoords] = await Promise.all([
                geocodeLocation(view.source, view.state, view.country, view.source_lat, view.source_long),
                geocodeLocation(view.destination, view.destinationState, view.destinationCountry, view.dest_lat, view.dest_long)
              ]);

              // Create source marker
              const sourceMarker: MapMarker = {
                id: `${view.id}-source`,
                position: sourceCoords,
                type: 'source',
                viewId: view.id,
                title: view.source,
                location: `${view.source}, ${view.state}`,
                status: view.status,
                color: view.color || '#3b82f6'
              };

              // Create destination marker
              const destMarker: MapMarker = {
                id: `${view.id}-dest`,
                position: destCoords,
                type: 'destination',
                viewId: view.id,
                title: view.destination,
                location: `${view.destination}, ${view.destinationState}`,
                status: view.status,
                color: view.color || '#3b82f6'
              };

              // Create connection line
              const connection: ConnectionLine = {
                id: view.id,
                sourcePosition: sourceCoords,
                destinationPosition: destCoords,
                distance: parseFloat(view.distance) || calculateDistance(sourceCoords, destCoords),
                status: view.status,
                color: view.color || '#3b82f6',
                viewData: view
              };

              newMarkers.push(sourceMarker, destMarker);
              newConnections.push(connection);

            } catch (viewError) {
              console.error(`Failed to process view ${view.id}:`, viewError);
              // Continue processing other views
            }
          }
          
          // Allow UI to update between batches
          await new Promise(resolve => setTimeout(resolve, 10));
        }

        setMarkers(newMarkers);
        setConnections(newConnections);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process map data');
        console.error('Map data processing error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    processViews();
  }, [views]);

  // Calculate map bounds
  const bounds = useMemo(() => {
    if (markers.length === 0) return null;

    const lats = markers.map(m => m.position.lat);
    const lngs = markers.map(m => m.position.lng);

    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    };
  }, [markers]);

  // Calculate statistics
  const stats = useMemo(() => {
    const statusCounts: Record<string, number> = {};
    let totalDistance = 0;

    connections.forEach(conn => {
      statusCounts[conn.status] = (statusCounts[conn.status] || 0) + 1;
      totalDistance += conn.distance;
    });

    return {
      totalViews: views.length,
      totalDistance,
      averageDistance: connections.length > 0 ? totalDistance / connections.length : 0,
      statusCounts
    };
  }, [views.length, connections]);

  return {
    markers,
    connections,
    isLoading,
    error,
    bounds,
    stats
  };
}; 